import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, Stack, Typography
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import clientPortalServices from 'app/services/client-portal-services';
import ImageViewer from 'app/widgets/Images/ImageViewer';
import SignatureCanvas from 'app/widgets/Shared/SignatureCanvas/SignatureCanvas';
import { formatReceiptTin } from 'app/utils/rut';
import { formatDateTime, headline, statusColor, stepTitle } from './shipmentText';

const DEFAULT_STEPS = ['Pedido recibido', 'En preparación', 'En camino', 'Entregado'];
const STEP_ICONS = [ReceiptLongOutlinedIcon, Inventory2OutlinedIcon, LocalShippingOutlinedIcon, CheckCircleOutlineIcon];

// Linea de tiempo: el paso actual lleva su icono, los anteriores un punto lleno y los que
// faltan un punto vacio.
const Timeline = ({ step, steps, cancelled }) => (
    <Box sx={{ position: 'relative', my: 3, mx: { xs: 1, sm: 3 } }}>
        <Box sx={{ position: 'absolute', top: 22, left: '12.5%', right: '12.5%', height: 4, bgcolor: 'divider', borderRadius: 2 }} />
        {!cancelled &&
            <Box sx={{ position: 'absolute', top: 22, left: '12.5%', width: `${(step / (steps.length - 1)) * 75}%`, height: 4, bgcolor: 'text.primary', borderRadius: 2 }} />}
        <Stack direction="row" sx={{ position: 'relative' }}>
            {steps.map((label, index) => {
                const Icon = STEP_ICONS[index] || CheckCircleOutlineIcon;
                const current = !cancelled && index === step;
                const done = !cancelled && index < step;
                return (
                    <Stack key={label} alignItems="center" sx={{ flex: 1 }}>
                        <Box sx={{ height: 48, display: 'flex', alignItems: 'center' }}>
                            {current ? (
                                <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'text.primary', color: 'background.paper',
                                           display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 2 }}>
                                    <Icon />
                                </Box>
                            ) : (
                                <Box sx={{ width: 16, height: 16, borderRadius: '50%',
                                           bgcolor: done ? 'text.primary' : 'background.paper',
                                           border: 3, borderColor: done ? 'text.primary' : 'divider' }} />
                            )}
                        </Box>
                        <Typography variant="caption" align="center"
                                    sx={{ mt: 1, fontWeight: current ? 700 : 400, color: current || done ? 'text.primary' : 'text.secondary' }}>
                            {label}
                        </Typography>
                    </Stack>
                );
            })}
        </Stack>
    </Box>
);

const Section = ({ title, children }) => (
    <Box sx={{ mt: 3 }}>
        <Typography variant="h4" sx={{ mb: 1.5 }}>{title}</Typography>
        {children}
    </Box>
);

const formatQty = (value) => (value === null || value === undefined ? '' : Number(value).toLocaleString('es-CL'));

const ShipmentDetailDialog = ({ card, steps, onClose }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [viewer, setViewer] = useState(null); // { title, images }

    useEffect(() => {
        if (!card) { setDetail(null); return; }
        let alive = true;
        setLoading(true);
        setDetail(null);
        clientPortalServices.getShipment(card.kind, card.id)
            .then((data) => { if (alive) setDetail(data); })
            .catch(() => {})
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [card]);

    // La firma llega como el JSON de trazos que guarda la app. Rota o vacia no se dibuja.
    const signatureStrokes = useMemo(() => {
        const raw = detail?.receipt?.signature;
        if (!raw) return null;
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            const usable = (Array.isArray(parsed) ? parsed : []).filter((stroke) => stroke?.points?.length > 1);
            return usable.length ? usable : null;
        } catch (error) {
            return null;
        }
    }, [detail]);

    const shown = detail || card;
    const ordersLabel = (shown?.orders || []).map((o) => o.code).filter(Boolean).join(', ');
    const hasDocuments = Boolean(detail?.documents?.length || detail?.signed_document || detail?.docs_images?.length);

    return (
        <Dialog fullWidth maxWidth="md" open={Boolean(card)} onClose={onClose}>
            <DialogContent sx={{ pt: 3 }}>
                {shown && (
                    <>
                        <Typography variant="body1" sx={{ color: statusColor(shown), fontWeight: 600 }}>
                            {shown.status} · {headline(shown)}
                        </Typography>
                        <Typography variant="h2" sx={{ mt: 1 }}>{stepTitle(shown)}</Typography>
                        {ordersLabel &&
                            <Typography variant="body2" color="text.secondary">
                                {shown.orders.length > 1 ? 'Pedidos' : 'Pedido'} {ordersLabel}
                                {shown.kind === 'delivery' ? ` · Entrega N° ${shown.code}` : ''}
                            </Typography>}

                        <Timeline step={shown.step} steps={steps || DEFAULT_STEPS} cancelled={shown.cancelled} />
                    </>
                )}

                {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

                {detail && (
                    <>
                        {detail.address &&
                            <Typography variant="body2" color="text.secondary">Dirección de entrega: {detail.address}</Typography>}

                        <Section title="Productos">
                            {detail.items?.length ? (
                                <Stack divider={<Divider flexItem />} spacing={1.5}>
                                    {detail.items.map((item, index) => (
                                        <Stack key={`${item.code}-${index}`} direction="row" spacing={2} alignItems="center">
                                            <Box sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: 1, bgcolor: 'action.hover',
                                                       display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {item.image_url
                                                    ? <Box component="img" src={item.image_url} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                    : <Inventory2OutlinedIcon sx={{ color: 'text.disabled' }} />}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body1">{item.product}</Typography>
                                                {item.code && <Typography variant="caption" color="text.secondary">{item.code}</Typography>}
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="body1">{formatQty(item.qty)} {item.unit || ''}</Typography>
                                                {item.delivered_qty !== null && item.delivered_qty !== undefined && item.delivered_qty !== item.qty &&
                                                    <Typography variant="caption" color="warning.main">Entregado: {formatQty(item.delivered_qty)}</Typography>}
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            ) : <Typography variant="body2" color="text.secondary">Sin detalle de productos.</Typography>}
                        </Section>

                        <Section title="Documentos">
                            {hasDocuments ? (
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {(detail.documents || []).map((doc) => (
                                        <Button key={doc.id} variant="outlined" size="small" startIcon={<DescriptionOutlinedIcon />}
                                                href={doc.url} target="_blank" rel="noopener noreferrer">
                                            {doc.kind || 'Documento'}{doc.number ? ` N° ${doc.number}` : ''}
                                        </Button>
                                    ))}
                                    {detail.signed_document &&
                                        <Button variant="outlined" size="small" color="success" startIcon={<TaskOutlinedIcon />}
                                                href={detail.signed_document.url} target="_blank" rel="noopener noreferrer">
                                            Documento firmado
                                        </Button>}
                                    {detail.docs_images?.length > 0 &&
                                        <Button variant="outlined" size="small" startIcon={<PhotoLibraryOutlinedIcon />}
                                                onClick={() => setViewer({ title: 'Documentos tributarios', images: detail.docs_images })}>
                                            Fotos del documento ({detail.docs_images.length})
                                        </Button>}
                                </Stack>
                            ) : <Typography variant="body2" color="text.secondary">Todavía no hay documentos para este pedido.</Typography>}
                        </Section>

                        {detail.receipt && (
                            <Section title="Recibido por">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={7}>
                                        <Typography variant="body1"><b>Nombre:</b> {detail.receipt.name || '—'}</Typography>
                                        <Typography variant="body1"><b>RUT:</b> {detail.receipt.tin ? formatReceiptTin(detail.receipt.tin) : '—'}</Typography>
                                        {detail.receipt.completed_at &&
                                            <Typography variant="body1"><b>Fecha:</b> {formatDateTime(detail.receipt.completed_at)}</Typography>}
                                        {detail.receipt.observations &&
                                            <Typography variant="body1"><b>Observaciones:</b> {detail.receipt.observations}</Typography>}
                                        {detail.partial && <Chip size="small" color="warning" label="Entrega parcial" sx={{ mt: 1 }} />}
                                    </Grid>
                                    <Grid item xs={12} sm={5} sx={{ textAlign: { sm: 'right' } }}>
                                        {signatureStrokes
                                            ? <SignatureCanvas height={200} width={200} signatureData={signatureStrokes} caption="Firma" />
                                            : <Typography variant="body2" color="text.secondary">Sin firma digital.</Typography>}
                                    </Grid>
                                </Grid>
                            </Section>
                        )}

                        {detail.package_photos?.length > 0 && (
                            <Section title="Fotos de bultos">
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {detail.package_photos.map((photo, index) => (
                                        <Box key={photo.id} component="img" src={photo.url} alt={`Bulto ${photo.position}`}
                                             onClick={() => setViewer({ title: 'Fotos de bultos', images: detail.package_photos.map((p) => ({ ...p, caption: `Bulto ${p.position}` })), startAt: index })}
                                             sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }} />
                                    ))}
                                </Stack>
                            </Section>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>

            <ImageViewer
                isOpen={Boolean(viewer)}
                title={viewer?.title}
                images={viewer?.images}
                startAt={viewer?.startAt || 0}
                onClose={() => setViewer(null)}
            />
        </Dialog>
    );
};

export default ShipmentDetailDialog;
